/**
 * Unity feed related functions
 */
import get from 'lodash/get';

import {transformDeeplink} from '~/src/utilities/helpers/deeplink';
import {transformPoll} from '~/src/utilities/helpers/poll';
import {transformAd} from '~/src/utilities/helpers/ad';
import {transformSlideshow} from '~/src/utilities/helpers/slideshow';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {ColorScheme, TeaserFormat} from '~/src/utilities/constants/formats';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {TeaserTypes, ContentType, ElementTypes, CustomSectionTypes} from '~/src/utilities/constants/types';
import {getJsonRequest} from '~/src/utilities/helpers/request';
import {createFormat} from '~/src/utilities/feed/format';
import {getSubset} from '~/src/utilities/feed/sitemap';
import {getPopularItems} from '~/src/utilities/helpers/popularity';
import {getLifestyleQuote} from '~/src/utilities/helpers/lifestyle';
import {groupSections, transformCustomSections, reorderSections, numberSections, formatSections, spanDefaultSection} from '~/src/utilities/helpers/section';
import {transformIframe, sanitizeStyles} from '~/src/utilities/helpers/teaser';
import {ContentStyle} from '../constants/style';
import {normalizeDate} from '../helpers/date';

const transformElement = (element, sitemap) => {
    element = transformDeeplink(element, sitemap);
    element = transformIframe(element);
    element = sanitizeStyles(element);
    return element;
};

/**
 * filter out elements based on various criteria:
 * - has style = 20MinutenNow
 *
 * @param {object} element feed element
 */
const filterElement = (element) => {
    return element.content?.styles?.name?.toLowerCase() !== ContentStyle.NOW;
};

/**
 * Unity list feeds, e.g. category feeds, return a list of elements.
 * Those elements can be various types of content, and sections.
 * For the purpose of rendering this data in a better way, this function
 * takes the data returned from the feed and transforms and enhances that data
 *
 * @param {object} data response data from the feed
 * @param {string} siteArea name of the siteArea used for teaser formats
 * @returns parsed and enhanced data
 */
const parseCategory = (data, siteArea, sitemap = []) => {
    const elements = get(data, 'content.elements', [])
        .filter(filterElement)
        .map((element) => transformElement(element, sitemap))
        .filter((element) => element.type !== TeaserTypes.IGNORE);

    const sections = groupSections(elements);

    transformCustomSections(sections);
    spanDefaultSection(sections);
    reorderSections(sections);
    numberSections(sections);
    formatSections(sections, siteArea, data);

    return {
        ...data,
        content: {
            ...data.content,
            elements: sections
        }
    };
};

/**
 * Parse article content and enhance or transform certain elements
 *
 * @param {object} article an article data node from the feed
 * @returns an article node with additional data
 */
const parseContent = ({content, type, ...rest}, {sitemap}) => {
    const contentElements = rest.type === ContentType.BREAKINGNEWS ? get(content, 'breakingnews.body.elements', []) : get(content, 'article.elements', []);

    const elements = [...contentElements].map((element) => {
        element = transformPoll(element);
        element = transformAd(element, 'inside-full-content');
        element = transformSlideshow(element, content.meta);

        return element;
    });

    const lifestyleCategories = getSubset(sitemap, SiteArea.LIFESTYLE);

    if (type === ContentType.ARTICLE && lifestyleCategories.some((item) => item.categoryId === get(content, 'meta.mainCategory'))) {
        type = ContentType.LIFESTYLE;
    }

    if (type === ContentType.BREAKINGNEWS) {
        elements.splice(
            0,
            0,
            {
                type: 'title',
                htmlText: get(content, 'breakingnews.title')
            },
            {
                type: 'lead',
                htmlText: get(content, 'breakingnews.lead.text')
            }
        );
    }

    // merge title-header and title elements
    const titleIndex = elements.findIndex((element) => element.type === ElementTypes.TITLE);
    if (titleIndex !== -1) {
        const mergedTitle = {
            type: ElementTypes.TITLE,
            htmlText: ''
        };

        const [titleElement] = elements.splice(titleIndex, 1, mergedTitle);

        const titleHeaderIndex = elements.findIndex((element) => element.type === ElementTypes.TITLE_HEADER);

        if (titleHeaderIndex !== -1) {
            const [titleHeaderElement] = elements.splice(titleHeaderIndex, 1);
            // for SEO and screenreaders
            const hiddenSpacing = '<span class="visually-hidden" aria-hidden="true" role="presentation">: </span>';
            mergedTitle.htmlText += `<span>${titleHeaderElement.htmlText}${hiddenSpacing}</span>`;
        }
        mergedTitle.htmlText += titleElement.htmlText;
    }

    // publication date
    const publishedTime = content.meta.updated || content.meta.published;
    if (publishedTime) {
        const index = (() => {
            if (type === ContentType.LIFESTYLE) {
                return elements.findIndex((element) => element.type === ElementTypes.TITLE);
            }
            return 0;
        })();
        elements.splice(index, 0, {
            type: 'publishDate',
            publishedTime: normalizeDate(publishedTime),
            isUpdated: content.meta.updated && content.meta.updated !== content.meta.published
        });
    }

    // authors
    const authors = content.meta.authors.filter((author) => author.type === 'user');
    if (authors.length) {
        const index = 1 + elements.findIndex((element) => element.type === ElementTypes.LEAD);
        elements.splice(index, 0, {
            type: 'authors',
            authors
        });
    }

    // agencies
    const agencies = content.meta.authors.filter((author) => author.type === 'agency');
    if (agencies.length) {
        elements.push({
            type: 'agencies',
            agencies
        });
    }

    return {
        ...rest,
        type,
        content: {
            ...content,
            article: {
                ...content.article,
                elements
            }
        }
    };
};

/**
 * Get relatedContent data from content feed and transform it for related stories
 *
 * @param {object} data data from the content feed
 * @returns {object[]} parsed and enhanced data
 */
const parseRelatedStories = (data) => {
    const elements = get(data, 'content.meta.relatedContent');
    elements.content.forEach((item, index) => (item.sortID = index));

    const relatedContent = {};
    relatedContent.showTitle = true;
    relatedContent.layout = SectionLayout.RELATED_CONTENT;
    relatedContent.elements = elements.content;

    return relatedContent;
};

/**
 * Transform a category feed into a format that can be rendered into related videos
 *
 * @param {object} data response data from the feed
 * @param {string} siteArea name of the siteArea used for teaser formats
 * @returns {object[]} parsed and enhanced data
 */
const parseRelatedVideos = (data, siteArea) => {
    const elements = get(data, 'content.elements', []);

    const index = elements.findIndex((element) => element.type === 'section' && element.layout !== SectionLayout.DEFAULT);
    if (index !== -1) {
        data.content.elements = elements.slice(0, index);
    }

    const section = elements.find((element) => element.type === 'section');
    section.layout = SectionLayout.RELATED_CONTENT;
    section.showTitle = true;

    return parseCategory(data, siteArea).content.elements.find((element) => element.type === 'section');
};

/**
 * Transform a series feed into a format that can be rendered into series videos
 *
 * @param {object} data response data from the feed
 * @returns {object[]} parsed and enhanced data
 */
const parseSeriesVideos = (data) => {
    const elements = get(data, 'content.elements', []);

    const index = elements.findIndex((element) => element.type === 'section' && element.layout !== SectionLayout.DEFAULT);
    return (index !== -1 ? elements.slice(0, index) : elements).filter((element) => element.type === TeaserTypes.VIDEOS).map(transformElement);
};

/**
 * Filters data for sliders
 *
 * @param {object} data response data from the feed
 * @returns parsed and enhanced data
 */
const parseSliderCategoryData = (data) => {
    const parsed = parseCategory(data);
    const [section] = parsed.content.elements;

    return {
        ...section,
        elements: section.elements
            .filter((element) => element.type !== TeaserTypes.AD)
            .map((element) => ({...element, format: createFormat(TeaserFormat.TEASER_SMALL)}))
    };
};

/**
 * Fetch additional data from feed for sliders
 *
 * @param {object} data response data from the feed
 * @returns {object} additional data
 */
const fetchAdditionalData = async (data) => {
    const sections = get(data, 'content.elements', []);
    for (let i = 0; i < sections.length; i++) {
        if (sections[i].fetchAdditionalData === true) {
            switch (sections[i].customType) {
                case CustomSectionTypes.LIFESTYLE_QUOTE:
                    await getLifestyleQuote(sections[i]);
                    break;
                case CustomSectionTypes.MOST_POPULAR:
                    sections[i].elements = (await getPopularItems(sections[i].options)) || [];
                    sections[i].elements.forEach((element) => {
                        element.format = createFormat(TeaserFormat.TEASER_SMALL, null, ColorScheme.PRIMARY);
                        element.type = TeaserTypes.POPULARITY;
                    });
                    break;
                default:
                    break;
            }
        }

        for (let j = 0; j < sections[i].elements.length; j++) {
            if (sections[i].elements[j].fetchAdditionalData === true) {
                switch (sections[i].elements[j].type) {
                    case TeaserTypes.MOST_POPULAR:
                        sections[i].elements[j].data = await getPopularItems(sections[i].elements[j].options);
                        break;
                    case TeaserTypes.SLIDER:
                        sections[i].elements[j].data = parseSliderCategoryData(await getJsonRequest(sections[i].elements[j].url));
                        break;
                    default:
                        break;
                }
            }
        }
    }
};

export {parseCategory, parseContent, parseRelatedStories, parseRelatedVideos, parseSeriesVideos, fetchAdditionalData};
